import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useToast } from './ToastContainer';
import { useFetch } from '../hooks/useFetchAuth';
import MappingFields, { ProcessingSwitch } from './MappingFields';
import SampleTable from './SampleTable';
import { NO_SHEET_ERROR, sheetMappingErrors } from '../utils/sheetMapping';

const SAMPLE_SIZE = 10;

function SheetMappingStep({ fileName, sheets, onBack, onNext, shopSign, client, year, week, initialData }) {
  const { addToast } = useToast();
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [mappingChoices, setMappingChoices] = useState([]);
  const [sheetConfigs, setSheetConfigs] = useState(() => {
    return initialData?.sheetConfigs || {};
  });
  const [validationErrors, setValidationErrors] = useState([]);

  // Fetch Mapping Choices
  const { data: fetchedMappingChoices } = useFetch(
    ['mappingChoices', shopSign, client],
    `${API_BASE_URL}/api/mapping-choices/${shopSign}/${client}`,
    {
        enabled: !initialData?.mappingChoices?.length && !!shopSign,
        select: (data) => {
            const excludedChoices = ['year', 'week'];
            return data.choices.filter(choice => !excludedChoices.includes(choice.value));
        }
    }
  );

  useEffect(() => {
      if (initialData?.mappingChoices?.length > 0) {
          setMappingChoices(initialData.mappingChoices);
      } else if (fetchedMappingChoices) {
          setMappingChoices(fetchedMappingChoices);
      }
  }, [initialData, fetchedMappingChoices]);

  // Fetch Preview Data
  const { data: fetchedPreviewData, error: fetchPreviewError } = useFetch(
      ['preview', fileName, selectedSheet],
      `${API_BASE_URL}/api/preview/${fileName}/${selectedSheet}?sample_size=${SAMPLE_SIZE}`,
      { enabled: !!fileName && !!selectedSheet }
  );

  useEffect(() => {
      if (fetchedPreviewData) {
          setPreviewData(fetchedPreviewData);
          setError(null);
      }
  }, [fetchedPreviewData]);

   useEffect(() => {
      if (fetchPreviewError) {
          console.error('Preview error:', fetchPreviewError);
          setError(fetchPreviewError.message);
          setPreviewData(null);
      }
  }, [fetchPreviewError]);


  // Afficher les erreurs de validation dans des toasts
  useEffect(() => {
    if (validationErrors.length > 0) {
      validationErrors.forEach((error, index) => {
        setTimeout(() => {
          addToast({
            title: "Erreur de validation",
            message: error,
            type: "error",
            duration: 6000
          });
        }, index * 100); // Délai pour éviter que tous les toasts apparaissent en même temps
      });
      // Réinitialiser les erreurs après les avoir affichées
      setValidationErrors([]);
    }
  }, [validationErrors, addToast]);

  const getCurrentSheetConfig = () => {
    return sheetConfigs[selectedSheet] || {
      isProcessing: false,
      columnMappings: {}
    };
  };

  const updateSheetConfig = (updates) => {
    setSheetConfigs(prev => ({
      ...prev,
      [selectedSheet]: {
        ...getCurrentSheetConfig(),
        ...updates
      }
    }));
  };

  const handleProcessingToggle = () => {
    updateSheetConfig({
      isProcessing: !getCurrentSheetConfig().isProcessing
    });
  };

  const handleMappingChange = (mappingValue, columnName) => {
    const currentMappings = getCurrentSheetConfig().columnMappings;

    if (!columnName) {
      const newMappings = { ...currentMappings };
      const columnToRemove = Object.entries(newMappings).find(([_, val]) => val === mappingValue)?.[0];
      if (columnToRemove) {
        delete newMappings[columnToRemove];
      }
      updateSheetConfig({ columnMappings: newMappings });
    } else {
      const newMappings = { ...currentMappings };
      Object.keys(newMappings).forEach(key => {
        if (newMappings[key] === mappingValue) {
          delete newMappings[key];
        }
      });
      if (columnName) {
        newMappings[columnName] = mappingValue;
      }
      updateSheetConfig({ columnMappings: newMappings });
    }
  };

  useEffect(() => {
    if (previewData && mappingChoices.length > 0 && selectedSheet) {
      const currentConfig = getCurrentSheetConfig();
      if (!currentConfig.columnMappings || Object.keys(currentConfig.columnMappings).length === 0) {
        const defaultMappings = {};

        if (year) {
          const yearMapping = mappingChoices.find(choice => choice.value === 'year');
          if (yearMapping) {
            defaultMappings['_year'] = 'year';
          }
        }

        if (week) {
          const weekMapping = mappingChoices.find(choice => choice.value === 'week');
          if (weekMapping) {
            defaultMappings['_week'] = 'week';
          }
        }

        updateSheetConfig({ columnMappings: defaultMappings });
      }
    }
  }, [previewData, mappingChoices, selectedSheet, year, week, client, shopSign]);


  // Sélectionner automatiquement la première feuille
  useEffect(() => {
    if (sheets && sheets.length > 0 && !selectedSheet) {
      handleSheetSelect(sheets[0]);
    }
  }, [sheets]); // Se déclenche quand sheets change

  const handleSheetSelect = (sheetName) => {
    setSelectedSheet(sheetName);
    setError(null);
  };

  // Helper pour formater la semaine sur deux chiffres
  const formatWeek = (weekNumber) => {
    return weekNumber.toString().padStart(2, '0');
  };

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg">
      <svg
        className="w-16 h-16 text-gray-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3 className="text-lg font-medium dark:text-white text-gray-900 mb-2">
        Erreur de lecture de la feuille
      </h3>
      <p className="text-gray-500 text-center max-w-sm">
        {error}
      </p>
      <button
        onClick={() => handleSheetSelect(selectedSheet)}
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                   transition-colors duration-200 flex items-center"
      >
        <svg
          className="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Réessayer
      </button>
    </div>
  );

  const renderMappingSection = () => (
    <MappingFields
      choices={mappingChoices}
      columns={previewData?.columns}
      mapping={getCurrentSheetConfig().columnMappings}
      onChange={handleMappingChange}
      disabled={!getCurrentSheetConfig().isProcessing}
      sampleRows={previewData?.sample_rows}
      period={{ year, week }}
    />
  );

  const validateMappings = () => {
    const configuredSheets = Object.entries(sheetConfigs).filter(([_, config]) => config.isProcessing);
    if (configuredSheets.length === 0) return [NO_SHEET_ERROR];
    return configuredSheets.flatMap(([sheetName, config]) => sheetMappingErrors(sheetName, config.columnMappings, mappingChoices));
  };

  const handleNext = () => {
    console.log('sheetConfigs', sheetConfigs);
    console.log('mappingChoices', mappingChoices);
    const errors = validateMappings();

    console.log('errors', errors);

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    onNext(sheetConfigs, mappingChoices);
  };
  const sheetSelectorStyle = {
    selected: 'bg-blue-50 text-blue-600 border-b-2 border-blue-600 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-400',
    unselected: 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800',
    selectedAndProcessed: 'bg-yellow-50 text-yellow-600 border-b-2 border-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400 dark:border-yellow-400',
    processed: 'bg-green-50 text-green-600 border-b-2 border-green-600 dark:bg-green-900/50 dark:text-green-400 dark:border-green-400',
  }
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold dark:text-white">Mapping des données</h2>
      </div>

      <div className="border rounded-lg overflow-hidden dark:border-gray-700">
        <div className="flex border-b dark:border-gray-700">
          {sheets?.map((sheet) => (
            <button
              key={sheet}
              onClick={() => handleSheetSelect(sheet)}
              className={`px-3 py-1.5 focus:outline-none ${
                selectedSheet === sheet
                  ? (getCurrentSheetConfig().isProcessing ? sheetSelectorStyle.selectedAndProcessed : sheetSelectorStyle.selected) 
                    : (sheetConfigs[sheet]?.isProcessing ? sheetSelectorStyle.processed : sheetSelectorStyle.unselected) 
              }`}
            >
              {sheet}
            </button>
          ))}
        </div>

        {error ? (
          renderErrorState()
        ) : (
          previewData && (
            <div className="flex flex-col gap-3">
              <div className="w-full p-3">
                <SampleTable columns={previewData.columns} rows={previewData.sample_rows} />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Total des lignes: {previewData.total_rows}
                </p>
              </div>

              <div className="border-t dark:border-gray-700 pt-3 px-3">
                <ProcessingSwitch checked={getCurrentSheetConfig().isProcessing} onChange={handleProcessingToggle} />

                <div className="mt-3 relative">
                  {!getCurrentSheetConfig().isProcessing && (
                    <div className="absolute inset-0 bg-gray-200/50 dark:bg-gray-700/50 backdrop-blur-[1px] z-10 rounded-lg" />
                  )}
                  <div className={`p-3 rounded-lg ${
                    !getCurrentSheetConfig().isProcessing ? 'pointer-events-none opacity-50' : ''
                  }`}>
                    {renderMappingSection()}
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleNext}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                     transition-colors duration-200 flex items-center gap-2"
        >
          Suivant
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default SheetMappingStep;